'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AutoFixLine, CubicReviewActivity } from './pr-v2-auto-fix-loop-model';
import type { PrV2AutoFixLoop, PrV2AutoFixLoopSurface } from './use-pr-v2-auto-fix-loop';
import { usePrV2AutoFixLoop } from './use-pr-v2-auto-fix-loop';
import { AutoFixLoopSwitch } from '@/components/auto-fix-loop/auto-fix-loop-switch';
import { AutoFixPauseStatus } from '@/components/auto-fix-loop/auto-fix-pause-status';
import { Explained } from '@/components/auto-fix-loop/explained';
import { useChangedInView, useChangeHold } from '@/components/motion/motion-hooks';
import { RollingNumber } from '@/components/motion/rolling-number';
import { Shimmer } from '@/components/ui/shimmer';
import { withAutoFixLoopLinkAttribution } from '@/lib/auto-fix-loop/links';
import { describeAutoFixLoopWork, describeLeftForYou, formatAutoFixLoopIssueCount } from '@/lib/auto-fix-loop/presentation';
import { usePrV2CommentsPopoverStore } from '@/lib/stores/pr-v2-comments-popover-store';
import { cn } from '@/lib/utils';
const ENTER_CLASS = 'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-0.5 motion-safe:duration-[180ms] motion-safe:ease-out';
const LANDING_HOLD_MS = 1200;

/** The loop stamps `noIssuesAt` when it finishes; clearing it (a new run, an enable) is no finish. */
function isFinishStamp(_previous: string | null, next: string | null) {
  return next !== null;
}

/** Resting colour, or the landing moment's brighter one, which settles back over a second. */
function landingTone(landing: boolean, resting: string) {
  return cn('transition-colors duration-1000', landing ? 'text-zinc-100' : resting);
}
function RunLink(t0) {
  const $ = _c(14);
  const {
    runUrl,
    label,
    surface,
    className,
    children
  } = t0;
  if (!runUrl) {
    let t1;
    if ($[0] !== children || $[1] !== className) {
      t1 = <span className={className}>{children}</span>;
      $[0] = children;
      $[1] = className;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== runUrl || $[4] !== surface) {
    t1 = withAutoFixLoopLinkAttribution(runUrl, surface);
    $[3] = runUrl;
    $[4] = surface;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = `${label} (opens the run in a new tab)`;
  let t3;
  if ($[6] !== className) {
    t3 = cn("inline-flex items-center gap-0.5", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowUpRight className="size-3 shrink-0 text-zinc-500" aria-hidden={true} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== children || $[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t5 = <a href={t1} target="_blank" rel="noopener noreferrer" aria-label={t2} className={t3} data-testid="pr-v2-auto-fix-loop-progress">{children}{t4}</a>;
    $[9] = children;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function RetryButton(t0) {
  const $ = _c(6);
  const {
    onRetry,
    isRetrying
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden={true} className="px-1 text-zinc-600">·</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = isRetrying ? "Retrying auto-fix" : "Retry auto-fix";
  const t3 = isRetrying ? "retrying" : "retry";
  let t4;
  if ($[1] !== isRetrying || $[2] !== onRetry || $[3] !== t2 || $[4] !== t3) {
    t4 = <>{t1}<button type="button" disabled={isRetrying} aria-label={t2} className="text-zinc-300 underline decoration-zinc-600 underline-offset-2 hover:text-zinc-100 disabled:cursor-default disabled:text-zinc-500 disabled:no-underline" onClick={onRetry}>{t3}</button></>;
    $[1] = isRetrying;
    $[2] = onRetry;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

/**
 * What a finish left for a person. cubic's open threads open the comments
 * popover; failing checks the loop set aside only add to the count, since the
 * checks themselves are listed with the pull request's CI.
 */
function LeftForYou(t0) {
  const $ = _c(19);
  const {
    line: t1,
    landing
  } = t0;
  const {
    count,
    failingCheckCount
  } = t1;
  let t2;
  if ($[0] !== count || $[1] !== failingCheckCount) {
    t2 = describeLeftForYou({
      openIssueCount: count,
      failingCheckCount
    });
    $[0] = count;
    $[1] = failingCheckCount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const left = t2;
  const total = count + failingCheckCount;
  if (count > 0) {
    let t3;
    if ($[3] !== count || $[4] !== failingCheckCount || $[5] !== left) {
      t3 = failingCheckCount > 0 ? `${left} left for you. Show the ${describeLeftForYou({
        openIssueCount: count,
        failingCheckCount: 0
      })}` : `Show ${left} left for you`;
      $[3] = count;
      $[4] = failingCheckCount;
      $[5] = left;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    const label = t3;
    let t4;
    if ($[7] !== label || $[8] !== landing || $[9] !== total) {
      t4 = <OpenCommentsButton count={total} label={label} landing={landing} />;
      $[7] = label;
      $[8] = landing;
      $[9] = total;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  const t3 = `${left} left for you`;
  let t4;
  if ($[11] !== landing) {
    t4 = landingTone(landing, "text-zinc-300");
    $[11] = landing;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== total) {
    t5 = <RollingNumber value={total} startFromZero={false} />;
    $[13] = total;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <Explained text={t3} align="end" className={t4}>{t5}{" for you"}</Explained>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

/** Opens the comments popover in the review tab bar, which lists the issues left. */
function OpenCommentsButton(t0) {
  const $ = _c(11);
  const {
    count,
    label,
    landing
  } = t0;
  const setCommentsPopoverOpen = usePrV2CommentsPopoverStore(_temp);
  let t1;
  if ($[0] !== landing) {
    t1 = cn("decoration-zinc-600 underline-offset-2 hover:underline", landingTone(landing, "text-zinc-300"));
    $[0] = landing;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== setCommentsPopoverOpen) {
    t2 = () => setCommentsPopoverOpen(true);
    $[2] = setCommentsPopoverOpen;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== count) {
    t3 = <RollingNumber value={count} startFromZero={false} />;
    $[4] = count;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <button type="button" aria-label={label} className={t1} onClick={t2}>{t3}{" for you"}</button>;
    $[6] = label;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

/**
 * The status words. Mounted once the facts they depend on are known, so the page opens on them
 * without motion; a later change of kind enters with a short rise, and a finish glows briefly.
 * The glow follows the loop's own finish rather than the kind, which an off-and-on flip of the
 * switch also changes.
 */
function _temp(state) {
  return state.setOpen;
}
function AutoFixLineStatus(t0) {
  const $ = _c(55);
  const {
    line,
    noIssuesAt,
    onRetry,
    isRetrying,
    surface
  } = t0;
  const entering = useChangedInView(line?.kind ?? null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      matches: isFinishStamp,
      holdMs: LANDING_HOLD_MS
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const landing = useChangeHold(noIssuesAt, t1);
  if (!line) {
    return null;
  }
  let words;
  bb0: switch (line.kind) {
    case "queued":
      {
        let t2;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <span className="text-zinc-500">queued</span>;
          $[1] = t2;
        } else {
          t2 = $[1];
        }
        words = t2;
        break bb0;
      }
    case "fixing":
      {
        let t2;
        if ($[2] !== line.work) {
          t2 = line.work.kind === "ci" ? describeAutoFixLoopWork(line.work) : `Fixing ${formatAutoFixLoopIssueCount(line.work.count, line.work.openCount, {
            processedCount: line.work.processedCount
          })}`;
          $[2] = line.work;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        let t3;
        if ($[4] !== line.work.count || $[5] !== line.work.kind || $[6] !== line.work.openCount || $[7] !== line.work.processedCount) {
          t3 = line.work.kind === "ci" ? "fixing CI" : `fixing ${formatAutoFixLoopIssueCount(line.work.count, line.work.openCount, {
            compact: true,
            processedCount: line.work.processedCount
          })}`;
          $[4] = line.work.count;
          $[5] = line.work.kind;
          $[6] = line.work.openCount;
          $[7] = line.work.processedCount;
          $[8] = t3;
        } else {
          t3 = $[8];
        }
        let t4;
        if ($[9] !== t3) {
          t4 = <Shimmer as="span" className="text-zinc-300">{t3}</Shimmer>;
          $[9] = t3;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        let t5;
        if ($[11] !== line.runUrl || $[12] !== surface || $[13] !== t2 || $[14] !== t4) {
          t5 = <RunLink runUrl={line.runUrl} surface={surface} label={t2}>{t4}</RunLink>;
          $[11] = line.runUrl;
          $[12] = surface;
          $[13] = t2;
          $[14] = t4;
          $[15] = t5;
        } else {
          t5 = $[15];
        }
        words = t5;
        break bb0;
      }
    case "finishing":
      {
        let t2;
        if ($[16] !== line.runUrl || $[17] !== surface) {
          t2 = <RunLink runUrl={line.runUrl} surface={surface} label="Finishing the previous run" className="text-zinc-500 hover:text-zinc-300">finishing</RunLink>;
          $[16] = line.runUrl;
          $[17] = surface;
          $[18] = t2;
        } else {
          t2 = $[18];
        }
        words = t2;
        break bb0;
      }
    case "for-you":
      {
        let t2;
        if ($[19] !== landing || $[20] !== line) {
          t2 = <LeftForYou line={line} landing={landing} />;
          $[19] = landing;
          $[20] = line;
          $[21] = t2;
        } else {
          t2 = $[21];
        }
        words = t2;
        break bb0;
      }
    case "fixed":
      {
        const t2 = line.runUrl;
        const t3 = `${line.count} fixed`;
        let t4;
        if ($[22] !== landing) {
          t4 = cn("decoration-zinc-600 underline-offset-2 hover:underline", landingTone(landing, "text-zinc-500"));
          $[22] = landing;
          $[23] = t4;
        } else {
          t4 = $[23];
        }
        let t5;
        if ($[24] !== line.count) {
          t5 = <span><RollingNumber value={line.count} startFromZero={false} />{" fixed"}</span>;
          $[24] = line.count;
          $[25] = t5;
        } else {
          t5 = $[25];
        }
        let t6;
        if ($[26] !== line.runUrl || $[27] !== surface || $[28] !== t3 || $[29] !== t4 || $[30] !== t5) {
          t6 = <RunLink runUrl={t2} surface={surface} label={t3} className={t4}>{t5}</RunLink>;
          $[26] = line.runUrl;
          $[27] = surface;
          $[28] = t3;
          $[29] = t4;
          $[30] = t5;
          $[31] = t6;
        } else {
          t6 = $[31];
        }
        words = t6;
        break bb0;
      }
    case "failed":
      {
        let t2;
        if ($[32] !== line.runUrl || $[33] !== surface) {
          t2 = <RunLink runUrl={line.runUrl} surface={surface} label="The last run failed" className="text-red-400 hover:text-red-300">failed</RunLink>;
          $[32] = line.runUrl;
          $[33] = surface;
          $[34] = t2;
        } else {
          t2 = $[34];
        }
        let t3;
        if ($[35] !== isRetrying || $[36] !== onRetry) {
          t3 = <RetryButton onRetry={onRetry} isRetrying={isRetrying} />;
          $[35] = isRetrying;
          $[36] = onRetry;
          $[37] = t3;
        } else {
          t3 = $[37];
        }
        let t4;
        if ($[38] !== t2 || $[39] !== t3) {
          t4 = <>{t2}{t3}</>;
          $[38] = t2;
          $[39] = t3;
          $[40] = t4;
        } else {
          t4 = $[40];
        }
        words = t4;
        break bb0;
      }
    case "paused":
      {
        let t2;
        if ($[41] !== isRetrying || $[42] !== line.allowance || $[43] !== line.canManageBilling || $[44] !== line.installationId || $[45] !== line.pause || $[46] !== onRetry || $[47] !== surface) {
          t2 = <AutoFixPauseStatus pause={line.pause} allowance={line.allowance} installationId={line.installationId} canManageBilling={line.canManageBilling} source={surface} onResume={onRetry} isResuming={isRetrying} className="text-amber-400" data-testid="pr-v2-auto-fix-loop-paused" />;
          $[41] = isRetrying;
          $[42] = line.allowance;
          $[43] = line.canManageBilling;
          $[44] = line.installationId;
          $[45] = line.pause;
          $[46] = onRetry;
          $[47] = surface;
          $[48] = t2;
        } else {
          t2 = $[48];
        }
        words = t2;
        break bb0;
      }
    default:
      {
        words = line satisfies never;
      }
  }
  const t2 = line.kind;
  const t3 = entering && ENTER_CLASS;
  let t4;
  if ($[49] !== t3) {
    t4 = cn("inline-flex items-center whitespace-nowrap", t3);
    $[49] = t3;
    $[50] = t4;
  } else {
    t4 = $[50];
  }
  let t5;
  if ($[51] !== line.kind || $[52] !== t4 || $[53] !== words) {
    t5 = <span key={t2} className={t4} data-testid="pr-v2-auto-fix-loop-status">{words}</span>;
    $[51] = line.kind;
    $[52] = t4;
    $[53] = words;
    $[54] = t5;
  } else {
    t5 = $[54];
  }
  return t5;
}

/** The line's name, which says the organization's Auto Fix PR usage on hover while it is enforced. */
export function PrV2AutoFixLoopLabel(t0) {
  const $ = _c(5);
  const {
    loop,
    className
  } = t0;
  if (!loop.allowanceText) {
    let t1;
    if ($[0] !== className) {
      t1 = <span className={className}>Auto-fix</span>;
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== className || $[3] !== loop.allowanceText) {
    t1 = <Explained text={loop.allowanceText} align="start" className={className} data-testid="pr-v2-auto-fix-allowance">Auto-fix</Explained>;
    $[2] = className;
    $[3] = loop.allowanceText;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/** What the loop is doing, in a word or two, once the facts it depends on are known. */
export function PrV2AutoFixLoopStatus(t0) {
  const $ = _c(6);
  const {
    loop
  } = t0;
  if (loop.resolution.kind !== "ready") {
    return null;
  }
  let t1;
  if ($[0] !== loop.isRetrying || $[1] !== loop.noIssuesAt || $[2] !== loop.resolution.line || $[3] !== loop.retry || $[4] !== loop.surface) {
    t1 = <AutoFixLineStatus line={loop.resolution.line} noIssuesAt={loop.noIssuesAt} onRetry={loop.retry} isRetrying={loop.isRetrying} surface={loop.surface} />;
    $[0] = loop.isRetrying;
    $[1] = loop.noIssuesAt;
    $[2] = loop.resolution.line;
    $[3] = loop.retry;
    $[4] = loop.surface;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

/** The auto-fix switch, which explains itself on hover. */
export function PrV2AutoFixLoopSwitch(t0) {
  const $ = _c(4);
  const {
    loop,
    id
  } = t0;
  let t1;
  if ($[0] !== id || $[1] !== loop.setEnabled || $[2] !== loop.switchState) {
    t1 = <AutoFixLoopSwitch id={id} state={loop.switchState} explain={true} size="sm" onCheckedChange={loop.setEnabled} />;
    $[0] = id;
    $[1] = loop.setEnabled;
    $[2] = loop.switchState;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

/** A refused flip's reason, under the line. */
export function PrV2AutoFixLoopRefusal(t0) {
  const $ = _c(5);
  const {
    loop,
    className
  } = t0;
  if (!loop.refusal) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("text-xs text-red-400", ENTER_CLASS, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== loop.refusal || $[3] !== t1) {
    t2 = <p className={t1}>{loop.refusal}</p>;
    $[2] = loop.refusal;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * Auto-fix as a line under cubic in the Reviewers list: "Auto-fix", what the
 * loop is doing in a word or two, and its switch. The switch is optimistic and
 * stays live while a request runs; it explains itself on hover. The line
 * hides on a pull request that cannot run auto-fix and never did; once it has
 * run, it stays with the switch disabled, and hovering the switch says why. A
 * refused flip shows its reason under the line.
 */
export function PrV2AutoFixLoopRow(t0) {
  const $ = _c(20);
  const {
    owner,
    repo,
    number,
    cubicReview
  } = t0;
  let t1;
  if ($[0] !== cubicReview || $[1] !== number || $[2] !== owner || $[3] !== repo) {
    t1 = {
      owner,
      repo,
      number,
      cubicReview,
      surface: "pr_page"
    };
    $[0] = cubicReview;
    $[1] = number;
    $[2] = owner;
    $[3] = repo;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const loop = usePrV2AutoFixLoop(t1);
  if (!loop) {
    return null;
  }
  const t2 = loop.switchState.checked ? "text-zinc-400" : "text-zinc-500";
  let t3;
  if ($[5] !== t2) {
    t3 = cn("shrink-0 whitespace-nowrap", t2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== loop || $[8] !== t3) {
    t4 = <PrV2AutoFixLoopLabel loop={loop} className={t3} />;
    $[7] = loop;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== loop) {
    t5 = <div className="ml-auto flex min-w-0 items-center gap-2"><PrV2AutoFixLoopStatus loop={loop} /><PrV2AutoFixLoopSwitch loop={loop} id="auto-fix-loop-toggle" /></div>;
    $[10] = loop;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="relative flex h-6 items-center gap-2 pl-7 pr-0.5 text-[13px] before:pointer-events-none before:absolute before:-top-2 before:left-[9.5px] before:h-5 before:w-3.5 before:rounded-bl before:border-b before:border-l before:border-zinc-700">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== loop) {
    t7 = <PrV2AutoFixLoopRefusal loop={loop} className="mt-1 pl-7" />;
    $[15] = loop;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div data-testid="pr-v2-auto-fix-loop">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}